import {
  isIdMatch,
  User,
  AdminUser,
  Org,
  type HeroAppId,
  type HeroPlatform,
  type MemberType,
  type PartyRole,
  type Product,
} from '@hero/schema';
import { appBusiness, type Business } from '../config/app-registry.config';
import {
  getReferralOrgGroupProperties,
  getReferralPersonPropertiesOnce,
} from './referral-properties';

type Props = { [key: string]: string | number | boolean | string[] | Props[] };

export type AppProperties = {
  app: HeroAppId;
  business: Business;
  platform: HeroPlatform;
  environment?: string;
  isAgent?: boolean;
  conversationId?: string;
};

export type UserProperties = {
  userRoles: PartyRole[];
  userMainType: PartyRole;
  currentOrgRoles?: PartyRole[];
  userMemberTypeInOrg?: MemberType;
  currentOrgId?: string;
};

export function getAppProperties(
  app: HeroAppId,
  platform: HeroPlatform,
  extra?: {
    environment?: string;
    isAgent?: boolean;
    conversationId?: string;
  },
): AppProperties {
  return {
    app,
    platform,
    business: appBusiness(app),
    ...(extra?.environment && { environment: extra.environment }),
    ...(extra?.isAgent && { isAgent: extra.isAgent }),
    ...(extra?.conversationId && { conversationId: extra.conversationId }),
  };
}

export function getUserProperties(user: User): UserProperties {
  const currentOrg = user.orgs.find(
    o => user.currentSignedInOrgId && isIdMatch(o._id, user.currentSignedInOrgId),
  );
  return {
    userRoles: user.roles,
    userMainType: user.roles[0] ?? 'individual_customer',
    ...(user.currentSignedInOrgId && { currentOrgId: user.currentSignedInOrgId.toString() }),
    ...(currentOrg?.roles?.length && { currentOrgRoles: currentOrg.roles }),
    ...(currentOrg?.memberType && { userMemberTypeInOrg: currentOrg.memberType }),
  };
}

interface PersonPropertiesConfig {
  app: HeroAppId;
  platform: HeroPlatform;
}

function personAppsFromMeta(
  user: User,
  current: { app: HeroAppId; platform: HeroPlatform },
): {
  apps: HeroAppId[];
  appPlatforms: string[];
  lastSeenApp: HeroAppId;
  lastSeenPlatform: HeroPlatform;
} {
  const rows = [...(user.meta?.presence?.apps ?? [])];
  if (!rows.some(r => r.app === current.app && r.platform === current.platform)) {
    rows.push({
      app: current.app,
      platform: current.platform,
      firstSeenAt: new Date(),
      lastSeenAt: new Date(),
    });
  }
  const last = user.meta?.lastSeenOnline;
  return {
    apps: [...new Set(rows.map(r => r.app))],
    appPlatforms: [...new Set(rows.map(r => `${r.app}:${r.platform}`))],
    lastSeenApp: last?.app ?? current.app,
    lastSeenPlatform: last?.platform ?? current.platform,
  };
}

/**
 * Stable person properties — sent via `$set` on every identify call.
 *
 * Current `app` / `platform` are event super-properties, not Person
 * scalars: last-write-wins would make a WhatsApp user who later opens
 * the dashboard look like they only live in the dashboard.
 *
 * "Which apps they have used" comes from `User.meta.presence.apps`.
 */
export const getPostHogPersonProperties = (user: User, config: PersonPropertiesConfig): Props => {
  const currentOrg = user.orgs.find(
    org => user.currentSignedInOrgId && isIdMatch(org._id, user.currentSignedInOrgId),
  );
  const presence = personAppsFromMeta(user, config);
  const userProps = getUserProperties(user);
  const props: Props = {
    id: user._id.toString(),
    name: user.name,
    phone: user.phone,
    preferredLanguage: user.preferredLanguage,
    orgsCount: user.orgs.length,
    ...presence,
    ...userProps,
    orgs: user.orgs.map(org => ({
      id: org._id.toString(),
      name: org.name,
      type: org.target,
      roles: org.roles,
      memberType: org.memberType,
    })),
  };
  if (currentOrg?.name) props['orgName'] = currentOrg.name;
  if (currentOrg?.target) props['orgType'] = currentOrg.target;
  return props;
};

/**
 * Immutable person properties — sent via PostHog's $set_once.
 * PostHog only writes these on the FIRST identify call for a user;
 * all subsequent calls with the same keys are silently ignored.
 * Safe to call on every identify — only the original values are kept.
 *
 * Pass `user` to also stamp the referral attribution persisted on
 * `User.meta.referral` (`referral_code`, `referral_partner_id`, …) so
 * every Person the clients identify can be broken down by the partner
 * who brought them. See `referral-properties.ts`.
 */
export const getPostHogPersonPropertiesOnce = (
  app: HeroAppId,
  platform: HeroPlatform,
  user?: Pick<User, 'meta'>,
): Props => ({
  initialApp: app,
  initialPlatform: platform,
  initialBusiness: appBusiness(app),
  signupDate: new Date().toISOString(),
  ...getReferralPersonPropertiesOnce(user?.meta?.referral),
});

/**
 * Org group properties — sent via `group('org', orgId, props)`.
 *
 * Callers pass the unified `Org` doc for the user's currently active
 * org (`AuthGetMeData.org`), or leave `org` undefined and we still
 * emit the essentials from the `User.orgs[i]` snapshot.
 */
export const getPostHogOrgProperties = (data: {
  user: User;
  /**
   * The full `Org` doc for the user's currently active org, when
   * the caller has it (typically `AuthGetMeData.org`). Optional —
   * when omitted the fields sourced from `Org` are simply skipped
   * and only the identity fields on the `User.orgs[i]` snapshot are
   * emitted.
   */
  org?: Org;
}): Props | null => {
  const { user, org } = data;
  const currentOrg = user.orgs.find(
    o => user.currentSignedInOrgId && isIdMatch(o._id, user.currentSignedInOrgId),
  );
  if (!currentOrg) return null;

  const props: Props = {
    name: currentOrg.name,
    orgType: currentOrg.target,
    orgRoles: currentOrg.roles,
  };
  if (currentOrg.memberType) props['memberType'] = currentOrg.memberType;

  if (org?.fleet?.total != null) props['vehiclesActualCount'] = org.fleet.total;

  const preferredTypes = org?.settings?.vehicle_preferred_types;
  if (preferredTypes && preferredTypes.length > 0) props['vehiclesPreferred'] = preferredTypes;

  if (org?.members?.all != null) props['membersCount'] = org.members.all;

  const presenceApps = org?.meta?.presence?.apps ?? [];
  if (presenceApps.length > 0) {
    props['apps'] = [...new Set(presenceApps.map(r => r.app))];
    props['appPlatforms'] = [...new Set(presenceApps.map(r => `${r.app}:${r.platform}`))];
  }
  if (org?.meta?.lastSeenOnline?.app) props['lastSeenApp'] = org.meta.lastSeenOnline.app;
  if (org?.meta?.lastSeenOnline?.platform)
    props['lastSeenPlatform'] = org.meta.lastSeenOnline.platform;

  // Referral attribution on the org (stamped by the backend at signup /
  // lead conversion). Group props are last-write-wins, but the stamp is
  // first-touch and never rewritten, so this stays stable.
  Object.assign(props, getReferralOrgGroupProperties(org?.meta?.referral));

  return props;
};

/** Commercial org-group props from the owner's subscription grants. */
export function getPostHogOrgCommercialProperties(
  subscriptions: Array<{ purchase: { product: Product; planCode: string } }>,
): { products: Product[]; planCodes: string[] } {
  return {
    products: [...new Set(subscriptions.map(s => s.purchase.product))],
    planCodes: [...new Set(subscriptions.map(s => s.purchase.planCode))],
  };
}

/** Admin panel person properties — sent via $set on every identify call */
export const getPostHogAdminPanelPersonProperties = (
  user: AdminUser,
  config: PersonPropertiesConfig,
) => {
  return {
    id: user._id.toString(),
    name: user.name,
    phone: user.phone,
    email: user.email,
    preferredLanguage: user.preferredLanguage,
    permissions: user.permissions.join(','),
    status: user.status,
    lastSeenApp: config.app,
    lastSeenPlatform: config.platform,
    userMainType: 'admin',
  };
};
